import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { LogOut, ArrowRight } from "lucide-react";
import HiredLogo from "@/components/brand/HiredLogo";

export default function AdminDashboard() {
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [plans, setPlans] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const nav = useNavigate();

  useEffect(() => {
    // ensure admin token active
    localStorage.setItem("hired_token_kind", "admin");
    api.get("/admin/dashboard").then(({data}) => setDash(data)).catch(() => nav("/admin/login"));
    api.get("/admin/customers").then(({data}) => setCustomers(data)).catch(() => {});
    api.get("/admin/employees").then(({data}) => setEmployees(data)).catch(() => {});
    api.get("/admin/plans").then(({data}) => setPlans(data)).catch(() => {});
  }, [nav]);

  const logout = () => {
    localStorage.removeItem("hired_admin_token");
    localStorage.setItem("hired_token_kind", "user");
    nav("/admin/login");
  };

  const openCustomer = async (business_id) => {
    const { data } = await api.get(`/admin/customers/${business_id}`);
    setSelectedCustomer(data);
  };

  return (
    <div className="min-h-screen bg-obsidian text-ivory">
      <header className="border-b border-line px-6 md:px-10 py-5 flex items-center justify-between">
        <Link to="/admin" className="flex items-center gap-3"><HiredLogo size={20} /><span className="text-steel text-xs tracking-widest ml-1">ADMIN</span></Link>
        <button data-testid="admin-logout" onClick={logout} className="text-steel text-sm hover:text-ivory inline-flex items-center gap-2"><LogOut size={14} /> Log out</button>
      </header>

      <div className="border-b border-line px-6 md:px-10">
        <div className="flex gap-6 max-w-7xl mx-auto">
          {["overview","customers","employees","plans"].map(t => (
            <button key={t} data-testid={`admin-tab-${t}`} onClick={() => { setTab(t); setSelectedCustomer(null); }} className={`py-4 text-sm tracking-wide border-b-2 ${tab===t ? "text-gold border-gold" : "text-smoke border-transparent hover:text-white"}`}>{t.toUpperCase()}</button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        {tab === "overview" && dash && (
          <>
            <div className="brand-eyebrow mb-4">HEALTH</div>
            <h1 className="brand-headline text-4xl md:text-6xl mb-10">Operations at a glance.</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <MetricCard label="Total customers" value={dash.total_customers} />
              <MetricCard label="Active customers" value={dash.active_customers} />
              <MetricCard label="Active employees" value={dash.active_employees} />
              <MetricCard label="Tasks today" value={dash.tasks_today} />
              <MetricCard label="Failed tasks" value={dash.failed_tasks} accent={dash.failed_tasks>0 ? "red" : ""} />
              <MetricCard label="Pending approvals" value={dash.pending_approvals} />
              <MetricCard label="AI calls" value={dash.ai_calls_total} />
              <MetricCard label="MRR (est.)" value={`$${dash.mrr}`} accent="gold" />
              <MetricCard label="Trials" value={dash.trials} />
              <MetricCard label="Cancellations" value={dash.cancellations} />
              <MetricCard label="Integration failures" value={dash.integration_failures} accent={dash.integration_failures>0 ? "red" : ""} />
            </div>
          </>
        )}

        {tab === "customers" && !selectedCustomer && (
          <>
            <div className="brand-eyebrow mb-4">TENANTS</div>
            <h1 className="brand-headline text-4xl md:text-6xl mb-10">Customers.</h1>
            <div className="border border-line divide-y divide-line">
              {customers.length === 0 && <div className="p-8 text-smoke text-sm">No customers yet.</div>}
              {customers.map(c => (
                <button key={c.business.id} data-testid={`admin-cust-${c.business.id}`} onClick={() => openCustomer(c.business.id)} className="w-full text-left p-5 hover:bg-white/[0.02] flex items-center justify-between">
                  <div>
                    <div className="text-white font-semibold">{c.business.name}</div>
                    <div className="text-smoke text-xs">{c.owner?.email} · {c.subscription?.status || "no subscription"}</div>
                  </div>
                  <div className="text-smoke text-xs">{c.employees_count} employees · {c.tasks_count} tasks · <ArrowRight size={14} className="inline" /></div>
                </button>
              ))}
            </div>
          </>
        )}

        {tab === "customers" && selectedCustomer && (
          <>
            <button onClick={() => setSelectedCustomer(null)} className="text-smoke text-sm hover:text-white mb-6">← Back to customers</button>
            <div className="brand-eyebrow mb-4">CUSTOMER</div>
            <h1 className="brand-headline text-4xl md:text-6xl mb-8">{selectedCustomer.business.name}</h1>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="border border-line p-6">
                <div className="brand-eyebrow mb-4">OWNER</div>
                <div className="text-white">{selectedCustomer.owner?.full_name}</div>
                <div className="text-smoke text-sm">{selectedCustomer.owner?.email}</div>
                <div className="text-smoke text-xs mt-2">Role: {selectedCustomer.owner?.role}</div>
              </div>
              <div className="border border-line p-6">
                <div className="brand-eyebrow mb-4">SUBSCRIPTION</div>
                {selectedCustomer.subscriptions.map(s => (
                  <div key={s.id} className="text-smoke text-sm mb-3">
                    <span className="text-white uppercase font-bold">{s.plan_slug}</span> · {s.status}
                  </div>
                ))}
              </div>
              <div className="border border-line p-6">
                <div className="brand-eyebrow mb-4">USAGE</div>
                <div className="text-white text-3xl brand-headline">{selectedCustomer.ai_usage_count}</div>
                <div className="text-smoke text-xs">AI calls to date</div>
              </div>
            </div>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionList title="EMPLOYEES" items={selectedCustomer.employees.map(e => ({key:e.id, main:e.display_name, sub:`${e.status} · hired ${new Date(e.hired_at).toLocaleDateString()}`}))} />
              <SectionList title="INTEGRATIONS" items={selectedCustomer.integrations.map(i => ({key:i.id, main:i.integration_name, sub:i.status}))} />
            </div>
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SectionList title="RECENT TASKS" items={selectedCustomer.recent_tasks.map(t => ({key:t.id, main:t.description, sub:`${t.status} · ${new Date(t.created_at).toLocaleString()}`}))} />
              <SectionList title="FAILURES" items={selectedCustomer.failures.map(f => ({key:f.id, main:f.error?.slice(0,140), sub:new Date(f.created_at).toLocaleString()}))} />
            </div>
          </>
        )}

        {tab === "employees" && (
          <>
            <div className="brand-eyebrow mb-4">CATALOG</div>
            <h1 className="brand-headline text-4xl md:text-6xl mb-10">AI Employees.</h1>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {employees.map(e => (
                <div key={e.slug} className="border border-line p-6">
                  <div className="brand-eyebrow">{e.industry?.toUpperCase()}</div>
                  <div className="brand-headline text-3xl mt-3">{e.name}</div>
                  <div className="text-smoke text-sm mt-2">{e.role_title}</div>
                  <div className="mt-4 flex gap-3">
                    <span className="text-[10px] tracking-widest font-bold text-gold">{e.status?.toUpperCase()}</span>
                    <span className="text-[10px] tracking-widest text-smoke">{e.workflows?.length || 0} workflows</span>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "plans" && (
          <PlansAdmin plans={plans} onReload={() => api.get("/admin/plans").then(({data}) => setPlans(data))} />
        )}
      </div>
    </div>
  );
}

function MetricCard({ label, value, accent }) {
  const color = accent === "gold" ? "text-gold" : accent === "red" ? "text-red-400" : "text-white";
  return (
    <div className="border border-line p-6">
      <div className="brand-eyebrow">{label.toUpperCase()}</div>
      <div className={`brand-headline text-4xl mt-3 ${color}`}>{value}</div>
    </div>
  );
}
function SectionList({ title, items }) {
  return (
    <div className="border border-line">
      <div className="border-b border-line px-5 py-3 brand-eyebrow">{title}</div>
      <div className="divide-y divide-line">
        {items.length === 0 && <div className="p-5 text-smoke text-sm">None.</div>}
        {items.map(i => (
          <div key={i.key} className="p-4">
            <div className="text-white text-sm">{i.main}</div>
            <div className="text-smoke text-xs mt-1">{i.sub}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PlansAdmin({ plans, onReload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const start = (p) => { setEditing(p.slug); setForm(p); };
  const save = async () => {
    await api.patch(`/admin/plans/${editing}`, {
      name: form.name, price_monthly: parseFloat(form.price_monthly),
      tagline: form.tagline, active: !!form.active,
    });
    setEditing(null); onReload();
  };
  return (
    <>
      <div className="brand-eyebrow mb-4">PRICING</div>
      <h1 className="brand-headline text-4xl md:text-6xl mb-10">Plans.</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {plans.map(p => (
          <div key={p.slug} className="border border-line p-6">
            <div className="brand-eyebrow">{p.tier}</div>
            {editing === p.slug ? (
              <>
                <input className="mt-3" value={form.name} onChange={e => setForm({...form, name: e.target.value})} />
                <input className="mt-3" type="number" step="0.01" value={form.price_monthly} onChange={e => setForm({...form, price_monthly: e.target.value})} />
                <input className="mt-3" value={form.tagline} onChange={e => setForm({...form, tagline: e.target.value})} />
                <label className="mt-3 flex items-center gap-2" style={{textTransform:'none',letterSpacing:'normal',color:'#fff',fontSize:'12px',fontWeight:400}}><input type="checkbox" style={{width:'16px',height:'16px'}} checked={!!form.active} onChange={e => setForm({...form, active: e.target.checked})} /> Active</label>
                <button data-testid={`plan-save-${p.slug}`} onClick={save} className="btn-gold mt-4 text-xs px-4 py-2">Save</button>
                <button onClick={() => setEditing(null)} className="text-smoke text-xs ml-2">Cancel</button>
              </>
            ) : (
              <>
                <div className="brand-headline text-3xl mt-3">{p.name}</div>
                <div className="text-white text-2xl mt-2">${p.price_monthly}<span className="text-smoke text-sm">/mo</span></div>
                <div className="text-smoke text-sm mt-2">{p.tagline}</div>
                <div className="mt-4 text-[11px] tracking-widest text-smoke">{p.active ? "ACTIVE" : "INACTIVE"}</div>
                <button data-testid={`plan-edit-${p.slug}`} onClick={() => start(p)} className="mt-4 text-gold text-xs hover:underline">Edit →</button>
              </>
            )}
          </div>
        ))}
      </div>
    </>
  );
}
